/**
 * Speaker notes / scripts per scene.
 *
 * Quotes from the user's script are reproduced verbatim; surrounding cues
 * are stage directions for the presenter.
 *
 * Markdown supported: **bold**, `code`, paragraphs (\n\n), `- bullet`, `1. ordered`.
 */
export const speakerNotes = {
  "opening-hold": `> "Since joining, my focus has been simple…"

> "Build an AI-powered system that increases output, reduces cost, and strengthens our position in the market."

> "There's actually a lot going on behind the scenes as well…"

> "Things like building an AI brain for the company… centralising knowledge, structuring data, laying foundations for what comes next."

> "But what I'm going to show you today… isn't roadmap."

> "It's what's already built, working, and ready to roll out. **Ready to sell!**"

> "We've gone from concept… to a full AI-powered property marketing engine in a matter of weeks."`,

  "quiz-question": `> "Quick quiz."

> "**How many videos are watched online… every day?**"`,

  "two-platforms": `> "200 billion. Instagram."
> "200 billion. YouTube Shorts."`,

  "400-billion": `> "400 billion videos. Per day."`,

  "12-trillion": `> "Stretch that out to a month — twelve trillion videos."

> "That's roughly **1,500 videos per person on Earth** — every single month."

> "Per year — one hundred and forty-four trillion."`,

  "video-dominates": `> "Video isn't growing…"
> "It's dominating."`,

  "and-yet": `> "And yet…"
> "In property…"
> "We're barely scratching the surface."

> "And the **opportunity that follows** is what this deck is about."`,

  "q1-so-far": `> "**Q1 so far.**"

> "From an idea on paper… to a live, scalable, AI-driven product suite. **In one quarter.**"

> "Here's the journey — visualised."`,

  "core-stack": `- **No extra effort from branches** — agents don't lift a finger.
- **No delays** — content goes out the same week the listing does.
- **Consistent output** — every property gets the same standard.

> "This turns every property into a **content engine**."`,

  "website-showcase": `> "It's not a roadmap — it's already on the website. Today."`,

  "package-table": `- **Social Go** = the standard bolt-on. Standard portrait edit, sneak-peek reel, social crops, logo branding.
- **Social Pro** = the premium bolt-on. Bespoke edit, branded stories, premium music library, MOQ on bespoke options.

> "Now — there are two more products that I want to add to this picture…"

> "**PresenTour** — branded, agent-led property tours. Available in Social Pro."
> "**NarraTour** — narrated tour videos. Also in Social Pro."

> "Both require onboarding — NarraTour needs a content session, PresenTour needs an Avatar Day. There are additional costs but the unit economics still work in our favour."

> "That's the **core stack** — already live, already shipping, with two new products coming online now."`,

  "tour-intro": `> "**PresenTour** is the **AI avatar-led tour** — a virtual presenter walking the property on camera."

> "**NarraTour** is the **narration tour** — same property, voiced over the visuals, no on-camera presenter."

> "Tour scripts and property updates… generated in seconds."

- **On-brand** — uses our tone of voice every time.
- **Structured** — hook, highlights, call-to-action — every script.
- **Ready to use** — presenter-ready, voice-ready, no edits needed.

> "No more wasting time."`,

  "how-it-works": `- **AI-generated, on-brand scripts in seconds** — the writer never starts from a blank page.
- **Presenter-ready, voice-ready, consistent output** — the same script feeds an avatar reading it on PresenTour and a narrator on NarraTour.

> "This is what it looks like in the product today — paste a Rightmove URL, the system generates an Intro, Living & Dining, the rest of the rooms, and we can edit any block in-line."`,

  "product-demos": `> "Consistent, on-brand scripts… in seconds."

1. **PresenTour** — avatar-led property tour
2. **NarraTour** — narrated walkthrough

> "**Watch what the system produces — straight from a fresh listing.**"`,

  "tradeoff-gone": `> "**Video Editing.**"

> "Imagine editing a full **Sneak Peak** — or even a full **property video** — before we leave the driveway."

> "(For those of you who haven't been in the field — photographers technically aren't supposed to park on driveways. So if we're editing the video before we leave the driveway… we're already finished before we should have moved.)"

> "Traditionally in the video space, speed comes at the cost of quality."
> "Cheap edits, slow turnarounds, or high-quality work that takes time and people."
> "That trade-off is now gone."

> "With the right **AI infrastructure**, **expertise**, and **creative control** — we can now increase **speed**, **quality**, and **efficiency** — all at the same time."`,

  "previously": `- **Video is expensive.**
- **Editing eats the day.**
- **Agents post less without the assets they actually need.**

> "Result? **No traction.**"

> "**What if we could automate video?**"

> "That's why **short-form video** — produced **at scale** — changes everything."

> "**All from the same content.**"`,

  "video-system-demo": `- **Upload Video Clips** (or load by Job ID) — clips come straight from the capture pipeline.
- **Output Formats** — Portrait 9:16, Portrait 4:5, Square 1:1, Landscape 16:9 — one upload, every aspect ratio.
- **Edit Mode** — Custom, Default, Short Edit, Long Edit, AI Timing, AI+ — pick the cadence, the system handles the cuts.
- **Live Preview** on the right — instant feedback before render.

> "One listing in. Multiple finished assets out — beat-mapped, on-brand, ready to ship."`,

  "now": `- **Editing cost** = low cost
- **Turnaround** = instant
- **Variations** = unlimited

> "Editing cost — low, because **your system** is doing it."

> "We want **video** on **everything**."`,

  "old-vs-your-world": `> "Old World — 1 property → 1 video → 1 post → limited reach."

> "Your World — 1 property → 10+ assets → multi-platform → constant presence."

> "Same listing. Ten times the output. Zero times the effort."`,

  "automated-editing": `> "Music is what makes a video work — and editing to it is the **biggest shift** we've made."

- **AI beat mapping** — every kick, snare, key change, detected.
- **Music-driven cuts** — edits land on the beat. Always.
- **Automated assembly** — multiple formats rendered in parallel from one source.

> "Editing is no longer a **manual task**. It's a **system**."

> "And that means we can **scale output without scaling cost**."`,

  "what-this-means": `> "This isn't just a product layer. It impacts **everyone in this room**."

- **RDs / AMs** → stronger client offering, easier upsell
- **Operations** → reduced manual workload
- **IT** → structured, scalable systems
- **Delivery** → faster turnaround without sacrificing quality

> "This is about making **the entire machine more efficient**."`,

  "show-me-the-money": `> "Show me the **money!**"

> "Time for some commercials."`,

  "baseline-current": `> "Let's look at where we are today…"

- **20,000 videos per year** — current annual volume.
- **Revenue per video: £45** — average selling price.
- **Edit cost per video: £5** — extremely competitive.`,

  "current-maths": `> "20,000 videos. Times five pounds in editing. Equals…"

> "**One hundred thousand pounds per year.** Just on editing."`,

  "guess-ai-cost": `> "What's the cost per edit using **AI tools**?"

> "Best guesses?"

> "Fifteen pence."

> "That's a **33× drop** — before we even change volume."`,

  "new-model": `> "New system brings that down to…"

> "Fifteen pence per video."

> "20,000 times £0.15. £3,000 per year. **All in.**"`,

  "saving-97k": `> "That's a saving of roughly…"

> "**£97,000.** Per year."

> "From £100,000 down to £3,000. Same volume. Same output. Just the editing replaced."`,

  "fifty-k-videos": `> "Now let's look at where we're trying to go…"

> "Fifty thousand videos a year."

> "Current model: 50,000 times £5 — that's **two hundred and fifty thousand pounds**."

> "New system: 50,000 times £0.15 — that's **seven and a half thousand**."`,

  "saving-240k": `> "That's over…"

> "**A quarter of a million pounds.** Saved annually. Just on editing."

> "And remember — that's just the editing line. We haven't touched the revenue side, the volume uplift, or the new product lines yet."`,

  "revenue-projection": `> "We've talked about saving money. Now let's flip it — and look at what this looks like as **revenue**."

- **Core video** — 50,000 × **£45** = **£2,250,000**
- **Bolt-on · Go / Pro** — 50,000 × **50%** × **£15** = **£375,000**

> "Total annual revenue line — **£2.625 million**."`,

  "foundation": `> "Even if we did nothing more than integrate this tool — we unlock a huge cost saving immediately."

> "But I'm already working on extending this into…"

- **Capture** — iPhone-led capture pipeline · agents shoot, system finishes.
- **Stabilisation** — AI-driven motion correction · phone footage, professional output.
- **Long form** — extended walk-throughs, YouTube-ready, premium tier.
- **Room intelligence** — auto-tag, auto-segment, auto-caption every space.

> "It's the **foundation** for scaling video across the entire business."`,

  "commercial-reality": `> "We are now in a position where…"

- **Cost per edit** drops dramatically (↓)
- **Output volume** increases (↑)
- **Turnaround time** shrinks (↓)
- **Product value** increases (↑)

> "Normally things like this don't happen together."

> "Now they do."`,

  "the-unlock": `> "And crucially…"

> "We are now ready to **onboard**."

> "**Mark** securing access to the **Work Management System**… that's the unlock."

> "Our **first meeting** is set up by **Darryl** — Ashtons in Harpenden, **20 May**."

> "That means this isn't theory anymore. This plugs directly into operations."`,

  "one-property-becomes": `> "I've always wanted to do a keynote and say these words…"

> "**There is…** *one property*… that becomes…"

1. **Sneak Peek** — fast, high energy. _"This will go in days…"_ → Reels / TikTok
2. **Full Tour** — long-form walk-through. → YouTube / Website
3. **Story Cuts** — 5-10 clips auto-cut. _"Kitchen → Garden → Bedroom."_ → Stories (daily presence)
4. **Social Images** — on-brand stills, in seconds. _"Same property · ten posts."_ → **Grid Post · LinkedIn · Stories**
5. **PresenTour / NarraTour** — presenter-ready property video. → Website / YouTube`,

  "leads-me-to-OLD-DUPLICATE": ``,

  // ── SECTION 6 · ONE MORE THING · SOCIAL WEEK ─────────────────────────
  "proposal-frame": `> "One more thing…"

> "Now — before I show you the next piece…"
> "**This is a proposal.** Not something confirmed or rolled out yet. It needs discussion, input, and alignment across the business."

> "But this is where it becomes more than production…"
> "It becomes **market control**. It's **ownership of local presence**."`,

  "quiz-time": `> "Quick quiz…"

> "Who here has clients that **struggle to stay consistent** on social?"

> "And who here has branches that should be **visible locally**… but just aren't?"

> "That's the **gap**…"
> "And **that** — is the product."`,

  "cost-today": `> "Let's just look at what branches are doing today."

- **Hiring a videographer** — typically around **£30,000** annually.
- Or a **social media manager** — similar cost, similar story.

> "I've spent time looking at what's actually being posted across the industry. Honestly — I can't believe how poor it is. Most of it barely registers."

> "I come from entertainment and sport — a world that *lives* on capturing an audience and pulling them in. Property won't ever match that ceiling — but it can go *miles* beyond where it sits today."

> "Eight years ago, sport had **no cameras backstage**. The behind-the-scenes content didn't exist. I was one of the first people doing it — at global events. Today, it's saturated."

> "Property is several years behind that curve. The window to **lead** it — instead of chasing it — is right now."`,

  "channels-dying": `> "Their **social channels are dying**."

- Inconsistent posting
- No time
- No strategy
- No real return
- Agent dependent
- Centralised content · not local

> "It's not a content problem. It's a **system** problem."

> "Think of the **local plumber**. They are **found**. They are **local**. They have a recognised reputation for good service."

> "Estate agents need to be exactly that — and right now, social isn't getting them there."`,

  "leads-me-to": `> "Which leads me to…"

> "Social Week. **Beta.**"`,

  "rough-maths": `> "Let's do some really **rough** maths…"`,

  "seven-days": `> "**Seven days of social.**"

> "**One press of a button.**"`,

  "social-week-demo": `- **Step 1 · Select & Assign** — they're here. Drag a property into a Template Slot.
- **Properties** pane on the left — all 9 properties from the branch already loaded, with the same 5-week filter the agent uses everywhere.
- **Template Slots** — Just Listed, Featured, Sold, Carousel — every weekly post type already set up. Each slot tells you which **aspect ratios** it supports (1:1 · 4:5 · 9:16) and the brand caption.
- **Auto-load from branch** — when an agent lists a new property, that property *flows* into next week's slot automatically.
- **Social Videos** — same pipeline already wired into video assets (count badge: 6).

> "One press · seven days · every channel."

> "Same shoot. Same property. **Programmed for the entire week.**"`,

  "5k-clients": `> "We've got around…"

> "**5,000 clients** across the portfolio."`,

  "ten-percent": `> "If just **10%** take this up…"

> "That's **500 branches**."`,

  "200k-monthly": `> "At, say, **£400 a month**…"

> "That's **£200,000** per month."`,

  "2-4-million": `> "That's…"

> "**£2.4 million a year.**"

> "From content… **we're already capturing**."

> "And remember — this **isn't just subscription revenue**."

> "Branches need **content** to power Social Week."
> "Which means **more video orders** — feeding our existing capture."
> "Which means **more core services** — pulling Social Go and Social Pro in."
> "Which means **more value per client** — deeper, stickier relationships."

> "So this doesn't just **add revenue**…"

> "It **amplifies everything we already do**."`,

  "twenty-percent": `> "And if uptake hits **20%**?"

> "That's **£4 to 5 million** annually."

> "And remember… that's just the **subscription**."`,

  "amplify-chain": `> "To power Social Week… branches need **content**."

- **Branches need content** → which means
- **More video orders** → fed into existing capture
- **More core services** → Social Go and Social Pro pulled in
- **More value per client** → deeper, stickier relationships

> "So this doesn't just **add revenue**…"

> "It **amplifies everything we already do**."`,

  "not-a-gimmick": `> "This isn't a **gimmick** product."
> "It's not a **side idea**."

> "This is a **£3 to 5 million arm of the business**."

> "**Built from content we already capture.**"`,

  "built-from-content": `> "Built **entirely** from content we **already have**."`,

  "infrastructure-control": `> "This is **bigger than revenue per product**."

> "This is about **infrastructure control**."

- **We own** the content. _(Property data, capture, footage — all upstream of us.)_
- **We generate** the assets. _(Scripts, edits, formats, variants — at zero marginal cost.)_
- **We distribute** the presence. _(Multi-platform, multi-format, automated cadence.)_
- **We embed** in the workflow. _(Sit inside ops, sit inside the listing flow.)_`,

  "dont-get-replaced": `> "Once we sit there…"

> "**We become irreplaceable.**"

> "Embedded. Sticky. **Structural.**"`,

  // ── SECTION 7 · CASTLE METAPHOR · WHY SOCIAL WEEK ────────────────────
  "why-castle": `> "Why **Social Week** protects us from the AI revolution."

> "Think of it like this…"`,

  "the-castle": `> "First… **the castle**."

> "That's our **core services** — the things we already deliver, every day."

- **Photos**
- **Floorplans**
- **Video**

> "This is what we're known for. The **structure** every client comes to us for. It stands on its own — it doesn't fall over."`,

  "the-knights": `> "Now — add the **archers**…"

> "**Social Go.** Our reach. Our volume. Firing outwards."

> "And the **knights**…"

> "**Social Pro.** Premium output. Sharper edges. Where we win the bigger ground."

> "They **attack** outwards… they **expand** our reach."

> "But here's the missing piece…"`,

  "the-moat": `> "And then… the **moat**."

> "**That's Social Week.**"

> "It doesn't attack. It doesn't expand."
> "It **protects** what we've already built…"
> "And it **holds** the people who are already inside."

> "**Retention. Protection.**"`,

  "hard-to-compete": `> "Castle… archers… knights… and a moat."

> "Together…"

> "That's **very hard to compete with**."

> "Most agencies have one or two of these. **We have the whole stack.** And Social Week is the piece that means we **stay** there."`,

  // ── SECTION 8 · THE FUTURE + CLOSE ───────────────────────────────────
  "future-hero": `> "What does the **future** look like?"

> "It looks like this."

> "Not roadmap. Already happening — in labs, in early-access programmes, in the AR layer that's quietly being built."`,

  "video-critical": `> "Video is the **closest digital format** to real-world experience."

> "And as interfaces evolve **beyond phones** — glasses, cars, screens, mixed reality — it's video that powers…"

- **Discovery** — how people find a property
- **Trust** — how they decide it's the one
- **Decision** — the moment they reach out

> "…at scale."`,

  "photographers-safe": `> "Photographers… are **safe**."

> "We're not removing humans from the loop. The team that captures, the team that styles, the team that walks the property — they're the reason any of this works. The system is what comes **after** they hit stop on the camera."

> "But the **client expectations** will change."

- **Faster turnaround** — clients won't tolerate the old timelines
- **Higher output** — they'll expect more, per shoot
- **More formats** — Reels, stories, hooks, longform — all from one capture
- **More value per shoot** — same fee, ten times the deliverable

> "And that's exactly what **this system enables**."

> "This isn't about **replacing people**."

> "It's about **removing inefficiency**."`,

  "not-replacing": `> "This isn't about **replacing people**."

> "It's about **removing inefficiency**."

> "Photographers are still **critical**."

- **Faster output**
- **More deliverables**
- **Higher perceived value**

> "And that's how we **stay competitive**."`,

  "connected-suite": `1. **Concept** — an idea on paper
2. **Foundation** — pipelines · capture · infrastructure
3. **Core Stack** — Social Go · Social Pro · live
4. **New Products** — PresenTour · NarraTour · in build
5. **Fully Connected AI Suite** — already delivering value

> "Before I get into what's actually in the boxes…"

> "I want to take you through the **journey** we've taken to get here."

> "**Concept** — an idea on paper a few months ago."

> "Then the **foundation** — pipelines, capture, infrastructure. None of it visible — all of it essential."

> "Then the **core stack** went live — Social Go, Social Pro, already shipping every day."

> "And now the **new products** — PresenTour, NarraTour — in build, days from launch."

> "And what we're left with… is a **fully connected AI suite**."

> "Not sold at scale yet…"

> "But already **delivering value at build stage**."`,

  "accelerating": `> "We're in an **incredibly fast-moving space** right now."

> "And it's **not just evolving**…"

> "It's **accelerating**."`,

  "iphone-22": `> "It might sound extreme…"

> "There's a real chance we **don't see** an **iPhone 22**."

> "Not because **technology slows down**…"

> "But because it **moves on to something else entirely**."

> "Ten, fifteen years ago…"

> "The smartphone felt **unbelievable**."

> "And now…"

> "It's just **normal**."

> "That was just **one chapter**."

> "And we're at the **start of the next one**."`,

  "just-one-chapter": `> "Ten, fifteen years ago…"

> "The smartphone felt **unbelievable**."

> "And now…"

> "It's just **normal**."

> "That was just **one chapter**."

> "And we're at the **start of the next one**."`,

  "build-for-it": `> "I'll be honest — **AI is coming**."

> "We don't have to react to it. We don't have to be scared of it."

> "We can **build for it**."`,

  "houses-always-sell": `> "Because while the technology changes…"

> "**One thing doesn't.**"

> "**Houses** will always **sell**."

> "**People** will always **move**."

> "And they will **always need** to present those homes in the best possible way."`,

  "where-we-win": `> "And **that's** where **we win**."

> "We're **not just keeping up** with change."

> "We're positioning ourselves **ahead** of it."

> "And I'm here… to make sure we **stay there**."`,

  "one-quarter": `> "In **one quarter**…"

> "We've gone from **concept**…"

> "…to a **connected, scalable system**."

> "Not fully rolled out yet…"

> "But already **delivering value in build phase**."`,

  "deploy-and-scale": `> "The opportunity now isn't to build **more**."

> "It's to…"

> "**Deploy.**"

> "And…"

> "**Scale.**"

> "Thank you."`,
};

export default speakerNotes;
